import '.a{display:grid;}.ssr-plugin'
import '.d5{gap:2rem;}.ssr-plugin'
import { styled } from 'inlines'
import { PageLayout } from '@app/pages/layout/PageLayout'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { useTranslations } from '@i18n/useLanguage'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { faSliders } from '@fortawesome/pro-regular-svg-icons'
import { useQuery } from '@based/sdk/react'
import { ServiceProviderListModel } from 'functions/app/service-provider/list'
import serviceProviderListFunction from 'functions/app/service-provider/list/based.config'
import { ServiceProviderFilter } from 'functions/app/service-provider/list/filters'
import { ServiceProviderCard } from '@app/components/blocks/card/ServiceProviderCard'
import { SearchField } from '@app/components/blocks/searchBar/SearchField'
import Row from '@app/components/layout/Row'
import { IconButton } from '@app/components/core/button/IconButton'
import { ServiceProviderFilterDrawer } from './ServiceProviderFilterDrawer'
import {
  useQueryStates,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
} from 'nuqs'
import { Empty } from '@app/components/blocks/empty/Empty'
import PersonWithBinocularsImage from '@app/assets/images/person_with_binoculars.png'
import useMediaQuery from '@app/util/useMediaQuery'
import { StaticPageSeo } from '@app/components/core/seo/StaticPageSeo'
import { pages } from '@app/util/links'

export const DiscoverServiceProviders = () => {
  const t = useTranslations().app.discoverServiceProviders
  const isMobile = useMediaQuery()
  const [showFilters, setShowFilters] = useState(false)
  const [searchBarInput, setSearchBarInput] = useState('')
  const [filterParams, setFilterParams] = useQueryStates({
    regions: parseAsArrayOf(parseAsInteger),
    types: parseAsArrayOf(parseAsInteger),
    search: parseAsString,
  })

  const searchTerm = filterParams.search ?? ''

  // Build filters object from URL params
  const filters: ServiceProviderFilter = useMemo(
    () => ({
      regions: filterParams.regions,
      types: filterParams.types,
    }),
    [filterParams.regions, filterParams.types],
  )

  // Sync search bar input with URL param
  useEffect(() => {
    setSearchBarInput(searchTerm)
  }, [searchTerm])

  const setSearchTerm = useCallback(
    (term: string) => {
      void setFilterParams({ search: term || null })
    },
    [setFilterParams],
  )

  const onFiltersChange = useCallback(
    (newFilters: ServiceProviderFilter) => {
      void setFilterParams({
        regions: newFilters.regions ?? null,
        types: newFilters.types ?? null,
      })
    },
    [setFilterParams],
  )

  const serviceProvidersQuery = useQuery(serviceProviderListFunction.name, {
    searchTerm: searchTerm,
    filters,
  })
  const serviceProviders: ServiceProviderListModel[] =
    serviceProvidersQuery.data ?? []

  return (
    <>
      <PageLayout gap="2rem">
        <StaticPageSeo
          path={pages.serviceProviders}
          pageTitle={t.title}
          description={t.description}
        />

        <div>
          <Title.H1 style={{ marginBottom: '0.5rem' }}>{t.title}</Title.H1>
          <Text.Medium>{t.description}</Text.Medium>
        </div>

        <Row alignItems="center">
          <SearchField
            placeholder={t.searchPlaceholder}
            searchBarInput={searchBarInput}
            searchTerm={searchTerm}
            setSearchBarInput={setSearchBarInput}
            setSearchTerm={setSearchTerm}
            style={{ maxWidth: '32rem' }}
          />
          <IconButton
            variant="secondary"
            icon={faSliders}
            onClick={() => setShowFilters(!showFilters)}
            style={{
              marginLeft: '1rem',
            }}
          />
        </Row>

        {serviceProviders.length === 0 ? (
          <Empty
            title={t.emptyTitle}
            imageSrc={PersonWithBinocularsImage}
            style={{ marginTop: '4rem' }}
          >
            {t.emptyDescription}
          </Empty>
        ) : (
          <div className="a d5"
            style={{
              gridTemplateColumns: isMobile
                ? 'repeat(1, 1fr)'
                : 'repeat(3, 1fr)',}}
          >
            {serviceProviders.map((serviceProvider) => (
              <ServiceProviderCard
                key={serviceProvider.uuid}
                slug={serviceProvider.slug}
                name={serviceProvider.name}
                description={serviceProvider.description}
                banner={serviceProvider.banner}
                avatar={serviceProvider.avatar?.src}
                types={serviceProvider.type}
              />
            ))}
          </div>
        )}
      </PageLayout>

      <ServiceProviderFilterDrawer
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={onFiltersChange}
      />
    </>
  )
}
